export * from './odds-api';
export * from './books';
import type { BookId } from './books';
// Distribution config (auto-generated from mlb-model)
export {
  PITCHER_OUTS_INNING_WEIGHTS,
  PITCHER_OUTS_CONTINUATION_PROBS,
  PITCHER_OUTS_CONTINUATION_MEAN_INNINGS,
  PITCHER_OUTS_SHIFT_SCALE,
  PITCHER_OUTS_TEMP_POWER,
  PITCHER_OUTS_TEMP_POWER_EARLY,
  PITCHER_OUTS_PER_INNING_SHIFT_SCALE,
} from './distribution-config';

export type Sport = 'nba' | 'mlb';

/** A viewer's access tier for a sport's paid data: what the server cuts each response to (the seed's view). */
export type AccessTier = 'public' | 'subscriber' | 'admin';

/** Default sport for routing/CTA fallbacks when no sport is otherwise chosen.
 *  Flip seasonally — currently NBA (MLB off-season). */
export const DEFAULT_SPORT: Sport = 'nba';

// The three ROI moments a game's board is priced at.
export const SNAPSHOT_TYPES = ['last_projection_update', '30_min_before', 'closing'] as const;
export type OddsSnapshotType = (typeof SNAPSHOT_TYPES)[number];

// The market codes: one list per sport, each code's type derived from its list (so no code exists in one and not the
// other). NBA in display order: base → combo → specialty.
export const NBA_MARKETS = ['pts', 'reb', 'ast', 'pra', 'pr', 'pa', 'ra', '3pm', 'blk', 'stl', 'to'] as const;
export type NBAMarketCode = (typeof NBA_MARKETS)[number];

// MLB in filter order: pitcher, base hitter, then specialty hitter markets.
export const MLB_MARKETS = [
  'pitcher_strikeouts', 'pitcher_hits_allowed', 'pitcher_walks', 'pitcher_earned_runs', 'pitcher_outs',
  'hits', 'home_runs', 'rbis', 'total_bases', 'runs_scored', 'stolen_bases', 'strikeouts', 'walks', 'hits_runs_rbis',
] as const;
export type MLBMarketCode = (typeof MLB_MARKETS)[number];

export type MarketCode = NBAMarketCode | MLBMarketCode;

// The game-line markets (game_lines.market): each sport's main moneyline, spread and total.
export const GAME_LINE_MARKETS = ['moneyline', 'spread', 'total'] as const;
export type GameMarket = (typeof GAME_LINE_MARKETS)[number];
// Where a game is in its life: scheduled until tip-off, then started, then final. A postponed game is rescheduled under the
// same id (scheduled again, with its new tip-off); a cancelled one is not played.
export type GameStatus = 'scheduled' | 'started' | 'final' | 'postponed' | 'cancelled';
// A game's status as `games` stores it: the one fact every reader asks (a feed's final is a started game). Who set it: the
// official schedule, the odds feed, or an analyst, whose word the feed never overrides.
export type StoredGameStatus = Exclude<GameStatus, 'final'>;
export type GameStatusSource = 'schedule' | 'feed' | 'analyst';
// A status that takes a game out of pregame.
export type ClosedGameStatus = Exclude<StoredGameStatus, 'scheduled'>;

// Predefined sort fields + any string (sportsbook names).
export type SortField = 'best_ev' | 'p_win' | 'delta' | 'name' | (string & {});
export type BookSide = 'over' | 'under';

export interface Game {
  id?: string; // Odds API event ID
  homeTeam?: string;
  awayTeam?: string;
  commenceTime?: string; // ISO
  opp: string; // opponent abbreviation
  date?: string;
  time?: string;
  isHome?: boolean;
}

export interface BookLine {
  book: BookId;
  side: BookSide;
  line: number;
  odds: number;
  ev?: number;      // undefined when no projection (≠ 0)
  pWin?: number;    // win-prob %; undefined when no projection (≠ 0)
  isRemoved: boolean; // in previous poll, not in current
  betLimit?: number; // max stake USD — exchanges only
  link?: string;    // deep-link; Caesars values may contain literal {state} placeholder
}

export interface BestLine {
  book: BookId;
  side: BookSide;
  line: number;
  odds: number;
  ev?: number;       // undefined when no projection (≠ 0)
  pWin?: number;
  isRemoved: boolean;
  betLimit?: number; // max stake USD — exchanges only
  link?: string;
}

export interface BestLines {
  over?: BestLine;
  under?: BestLine;
}

export interface PlayerRow {
  id: string;
  name: string;
  team: string;
  position?: string;
  imageUrl?: string;
  game?: Game;
  market: MarketCode;
  projection: number | null; // null = no projection from Google Sheets
  minutesProjection?: number | null;
  books: BookLine[];
  best: BestLine; // legacy; prefer bestLines
  bestLines?: BestLines;
  updatedAt: string;
}

/** Structured MLB lineup. Batters: own team; pitchers: opposing team. */
export interface LineupInfo {
  status: 'Projected' | 'Confirmed';
  team: string;
  names: string[];
  /** YYYY-MM-DD (ET) — distinguishes lineups on consecutive game days. */
  gameDate: string;
}

/** Why a market's projection is hidden from subscribers: its team is not released, the sport hides the market, or
 *  the analyst hid it for the player. */
export type HiddenBy = 'team' | 'sport' | 'player';

/** Player with all markets, for the selection view. */
export interface PlayerWithMarkets {
  id: string;
  name: string;
  team: string;
  position?: string;
  injury?: string;
  imageUrl?: string;
  game?: Game;
  projectedMinutes?: number | null;
  teamNotes?: string; // free-form note from Sheets C13 (when not a lineup status)
  playerNotes?: string; // NBA: the analyst's note on the player in this game
  lineup?: LineupInfo; // MLB-only (own team for batters, opposing for pitchers)
  markets: {
    [K in MarketCode]?: {
      projection: number | null; // null = no projection from Google Sheets
      projectedMinutes?: number | null; // activity: NBA mins, MLB batter PA, pitcher PC
      teamNotes?: string; // TWPs: pitcher vs batter may differ
      lineup?: LineupInfo; // TWPs: pitcher market = opposing, batter market = own
      pmf?: readonly number[]; // the priced distribution (NBA, browser-priced): the Price Calculator prices other lines from it
      /** Admin rows only: why subscribers don't see this market's projection and EV (its team not released, the
       *  sport's hidden markets, the analyst's player hide). Stripped with the EV for every other tier. */
      hiddenBy?: HiddenBy[];
      books: BookLine[];
      bestLines: BestLines;
    };
  };
  bestMarket?: { // highest-EV market
    market: MarketCode;
    ev: number;
    line: BestLine;
  };
  /** NBA: the player's game has questionable players, so its EV rests on the viewer's OUT chips (spec §4.4). */
  scenario?: { out: string[]; outPlayerIds: string[]; modeled: boolean };
  /** NBA: the player is one of their game's questionable players (the Q badge). */
  questionable?: boolean;
  updatedAt: string;
}

// The single-stat NBA markets: every other NBA market is a sum of these.
export const NBA_STAT_MARKETS = ['pts', 'reb', 'ast', '3pm', 'stl', 'blk', 'to'] as const satisfies readonly NBAMarketCode[];
export type NBAStatMarketCode = (typeof NBA_STAT_MARKETS)[number];

// The single-stat markets each NBA market sums: a combo is the sum of its parts.
export const NBA_MARKET_PARTS: Record<NBAMarketCode, readonly NBAStatMarketCode[]> = {
  pts: ['pts'], reb: ['reb'], ast: ['ast'], '3pm': ['3pm'], stl: ['stl'], blk: ['blk'], to: ['to'],
  pra: ['pts', 'reb', 'ast'], pr: ['pts', 'reb'], pa: ['pts', 'ast'], ra: ['reb', 'ast'],
};

// Each single-stat market's key in a stored NBA game log.
export const NBA_GAME_LOG_KEY: Record<NBAStatMarketCode, string> = {
  pts: 'pts', reb: 'reb', ast: 'ast', '3pm': 'fg3m', stl: 'stl', blk: 'blk', to: 'tov',
};

/** Markets carried by a Simulation Projection Release. */
export const MLB_RELEASE_PITCHER_MARKETS = [
  'pitcher_strikeouts', 'pitcher_hits_allowed', 'pitcher_walks',
  'pitcher_earned_runs', 'pitcher_outs',
] as const satisfies readonly MLBMarketCode[];

export const MLB_RELEASE_HITTER_MARKETS = [
  'hits', 'home_runs', 'rbis', 'strikeouts', 'total_bases', 'stolen_bases',
  'walks', 'runs_scored', 'hits_runs_rbis',
] as const satisfies readonly MLBMarketCode[];

export const MLB_RELEASE_MARKETS = [
  ...MLB_RELEASE_HITTER_MARKETS,
  ...MLB_RELEASE_PITCHER_MARKETS,
] as const satisfies readonly MLBMarketCode[];


// Short labels for table cells and cards.
export const MARKET_NAMES: Record<MarketCode, string> = {
  // NBA
  pts: 'PTS',
  reb: 'REB',
  ast: 'AST',
  pra: 'PRA',
  '3pm': '3PM',
  blk: 'BLK',
  stl: 'STL',
  to: 'TO',
  pr: 'PR',
  pa: 'PA',
  ra: 'RA',
  // MLB
  hits: 'Hits',
  home_runs: 'HR',
  rbis: 'RBIs',
  strikeouts: 'K',
  total_bases: 'TB',
  stolen_bases: 'SB',
  walks: 'BB',
  runs_scored: 'Runs',
  hits_runs_rbis: 'H+R+RBI',
  pitcher_strikeouts: 'K',
  pitcher_hits_allowed: 'Hits',
  pitcher_walks: 'BB',
  pitcher_earned_runs: 'ER',
  pitcher_outs: 'Outs',
};

export function getMarketsForSport(sport: Sport): readonly MarketCode[] {
  return sport === 'mlb' ? MLB_MARKETS : NBA_MARKETS;
}

export function getMarketName(market: MarketCode): string {
  return MARKET_NAMES[market];
}

/** Full NBA market names for filters and tabs. */
const NBA_FILTER_NAMES: Partial<Record<NBAMarketCode, string>> = {
  pts: 'Points',
  reb: 'Rebounds',
  ast: 'Assists',
  pra: 'Pts+Reb+Ast',
  pr: 'Pts+Reb',
  pa: 'Pts+Ast',
  ra: 'Reb+Ast',
  blk: 'Blocks',
  stl: 'Steals',
  to: 'Turnovers',
};

/** Disambiguated market names for filter chips (e.g. "Pitcher K" vs "Batter K") */
const MLB_FILTER_NAMES: Partial<Record<MLBMarketCode, string>> = {
  hits: 'Batter Hits',
  strikeouts: 'Batter K',
  walks: 'Batter BB',
  pitcher_strikeouts: 'Pitcher K',
  pitcher_hits_allowed: 'Pitcher Hits',
  pitcher_walks: 'Pitcher BB',
};

/** Display name for filter chips and tabs (full for NBA, disambiguated for MLB). */
export function getMarketFilterName(market: MarketCode): string {
  return MLB_FILTER_NAMES[market as MLBMarketCode] ?? NBA_FILTER_NAMES[market as NBAMarketCode] ?? MARKET_NAMES[market] ?? market;
}

/** Full, human-readable market names for verbose surfaces (e.g. the mobile odds
 *  expansion rows). Batter/pitcher codes are already side-specific, so no
 *  "Batter"/"Pitcher" prefix is needed. */
const MARKET_LONG_NAMES: Record<MarketCode, string> = {
  // NBA
  pts: 'Points',
  reb: 'Rebounds',
  ast: 'Assists',
  pra: 'Pts + Reb + Ast',
  '3pm': '3-Pointers Made',
  blk: 'Blocks',
  stl: 'Steals',
  to: 'Turnovers',
  pr: 'Pts + Reb',
  pa: 'Pts + Ast',
  ra: 'Reb + Ast',
  // MLB
  hits: 'Hits',
  home_runs: 'Home Runs',
  rbis: 'RBIs',
  strikeouts: 'Strikeouts',
  total_bases: 'Total Bases',
  stolen_bases: 'Stolen Bases',
  walks: 'Walks',
  runs_scored: 'Runs',
  hits_runs_rbis: 'Hits + Runs + RBIs',
  pitcher_strikeouts: 'Strikeouts',
  pitcher_hits_allowed: 'Hits Allowed',
  pitcher_walks: 'Walks',
  pitcher_earned_runs: 'Earned Runs',
  pitcher_outs: 'Outs',
};

/** Full spelled-out market name. Falls back to the abbreviation, then the code. */
export function getMarketLongName(market: MarketCode): string {
  return MARKET_LONG_NAMES[market] ?? MARKET_NAMES[market] ?? market;
}

/** Activity-metric label per sport (mins / PC / PA). */
export function formatActivityMetric(
  sport: Sport,
  value: number,
  market?: MarketCode
): string {
  if (sport === 'mlb') {
    const isPitcher = market?.startsWith('pitcher_');
    // PC reads as a whole-pitch number (analyst PCs are integers and the
    // model mean's decimals are noise); PA keeps hundredths — exposure
    // tilts move it in meaningful sub-tenth steps.
    return isPitcher ? `${Math.round(value)} PC` : `${value.toFixed(2)} PA`;
  }
  return `${value} mins`;
}

/** Win probability (0-100) → fair American odds. */
export function pWinToFairAmerican(pWin: number): number {
  const p = pWin / 100;
  if (p <= 0 || p >= 1) return 0;
  return p >= 0.5 ? Math.round(-100 * p / (1 - p)) : Math.round(100 * (1 - p) / p);
}

// Runtime zod schemas live in '@sharpr/types/validation' (subpath export) so the
// main barrel stays zod-free — client islands import display helpers/constants from here
// and must not pull zod into first-load JS.

export type {
  Theme,
  ResolvedTheme,
  ThemeContextValue,
  ThemeProviderProps
} from './ui';

import type { Theme } from './ui';

/** database: user_sport_preferences (one row per (user_id, sport)). */
export interface SportPreferences {
  hidden_markets: MarketCode[]; // empty = show everything
}

// Per-book participation. SOURCE OF TRUTH (user_preferences.sportsbook_participation,
// sparse map; absent key = 'full'). full = shown + best-line eligible; reference =
// shown, not eligible; off = hidden (and not eligible).
export type SportsbookParticipation = 'full' | 'reference' | 'off';

/** database: user_preferences, with sportPreferences composed from user_sport_preferences. */
export interface UserPreferences {
  id: string;
  user_id: string;
  ordered_sportsbooks: BookId[]; // column display order; absent books append at end
  // SOURCE OF TRUTH for visibility + best-line eligibility. Sparse: only non-'full' keyed.
  sportsbook_participation: Record<BookId, SportsbookParticipation>;
  hidden_sportsbooks: BookId[]; // DERIVED (read-only): books with state 'off'
  excluded_from_best_line: BookId[]; // DERIVED (read-only): state 'off' OR 'reference'
  theme: Theme;
  // Composed from user_sport_preferences rows; missing key = defaults.
  sportPreferences: Partial<Record<Sport, SportPreferences>>;
  created_at: string;
  updated_at: string;
}

export * from './subscription';
export * from './oddsFeed';
